// src/components/SortableSubtaskItem.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Trash2, GripVertical, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { normalizeUrl } from '../utils/utils';

function SortableSubtaskItem({ 
  index, 
  subtask,
  onToggleComplete,
  onDelete,
  onUpdateLink,
  loading 
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `subtask-${index}` });

  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkInput, setLinkInput] = useState(subtask.link || '');
  const linkInputRef = useRef(null);

  const subtaskText = typeof subtask === 'string' ? subtask : subtask.text;
  const isCompleted = subtask.completed;
  const hasLink = subtask.link && subtask.link.trim() !== '';

  useEffect(() => {
    if (showLinkInput && linkInputRef.current) {
      linkInputRef.current.focus();
    }
  }, [showLinkInput]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleLinkClick = (e) => {
    e.stopPropagation();
    if (hasLink) {
      // Normalize URL to ensure it has a protocol before opening
      const normalizedLink = normalizeUrl(subtask.link);
      // Open link in new tab
      window.open(normalizedLink, '_blank', 'noopener,noreferrer');
    } else {
      // Toggle input field
      setShowLinkInput(!showLinkInput);
      setLinkInput(subtask.link || '');
    }
  };

  const handleSaveLink = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onUpdateLink(index, linkInput.trim());
    setShowLinkInput(false);
  };

  const handleCancelLink = (e) => {
    e.stopPropagation();
    setLinkInput(subtask.link || '');
    setShowLinkInput(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveLink(e);
    } else if (e.key === 'Escape') {
      handleCancelLink(e);
    }
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex flex-col gap-2 p-2 rounded-md ${
        isCompleted
          ? 'opacity-50 bg-gray-100 dark:bg-gray-800'
          : 'bg-white dark:bg-gray-800'
      } ${isDragging ? 'shadow-lg z-50' : ''}`}
    >
      {/* Main Row */}
      <div className="flex items-center gap-3">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 touch-none"
          aria-label="Drag to reorder"
        >
          <GripVertical className="w-5 h-5" />
        </button>

        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isCompleted}
          onChange={onToggleComplete}
          disabled={loading}
          className="w-4 h-4 text-primary rounded focus:ring-primary"
        />

        {/* Text */}
        <span
          className={`flex-1 ${
            isCompleted
              ? 'line-through text-gray-500 dark:text-gray-400'
              : 'text-gray-900 dark:text-white'
          }`}
        >
          {subtaskText}
        </span>

        {/* Link Button */}
        <button
          onClick={handleLinkClick}
          disabled={loading}
          className={`p-1 rounded transition-colors disabled:opacity-50 ${
            hasLink
              ? 'text-blue-500 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30'
              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
          aria-label={hasLink ? 'Open link' : 'Add link'}
          title={hasLink ? 'Open link in new tab' : 'Add link'}
        >
          {hasLink ? (
            <ExternalLink className="w-4 h-4" />
          ) : (
            <LinkIcon className="w-4 h-4" />
          )}
        </button>

        {/* Delete Button */}
        <button
          onClick={onDelete}
          disabled={loading}
          className="p-1 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors disabled:opacity-50"
          aria-label="Delete subtask"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Link Input Field */}
      {showLinkInput && (
        <div className="ml-12 flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-200">
          <input
            ref={linkInputRef}
            type="url"
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Paste URL here..."
            className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={handleSaveLink}
            className="px-2 py-1 text-xs bg-primary hover:bg-primary/90 text-white rounded-md transition-colors"
            title="Save link (Enter)"
          >
            Save
          </button>
          <button
            onClick={handleCancelLink}
            className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
            title="Cancel (Esc)"
          >
            Cancel
          </button>
        </div>
      )}
    </li>
  );
}

export default SortableSubtaskItem;

